/**
 * Pièces à fournir (`/extranet/:tenantSlug/pieces-attendues`, fiche 22780):
 * page header « Pièces à fournir » / « {n} pièces en attente » and the
 * « En attente » list (`extranet.pendingPiecesListCard`) fed by
 * `WS2_EA_TAB_PMQ`.
 *
 * Rule context: the extranet base variables + `pending_pieces_count`
 * (undefined while loading / on error) — see `pendingPiecesContextSchema`.
 */

import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { RuleContextProvider } from '@components/shared/configurable';

import {
  fetchPendingPieces,
  pendingPiecesQueryKey,
  type PendingPiece,
} from '../api/piecesService';
import { useExtranetAuthStore } from '../auth/store';
import { PageHeader } from '../components/PageHeader';
import { PENDING_PIECES_TEXT } from '../components/pieces/pendingPieceItems';
import {
  PendingPiecesListCardConfigurable as PendingPiecesListCard,
} from '../components/pieces/register';
import { useExtranetBaseRuleContext } from '../contextVariables';
import { EXTRANET_PAGE_TEXT, extranetPath } from '../navigation';

const NO_PIECES: readonly PendingPiece[] = [];

export function PendingPiecesPage() {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const registrySlug = useExtranetAuthStore((s) => s.registrySlug);
  const sessionId = useExtranetAuthStore((s) => s.sessionId);
  const baseRuleContext = useExtranetBaseRuleContext();

  const query = useQuery<PendingPiece[]>({
    queryKey: pendingPiecesQueryKey(registrySlug, sessionId),
    queryFn: () => fetchPendingPieces(registrySlug),
    enabled: registrySlug !== null,
    retry: false,
  });

  const count = query.data?.length;
  const ruleContext = useMemo(
    () => ({ ...baseRuleContext, pending_pieces_count: count }),
    [baseRuleContext, count],
  );

  const text = EXTRANET_PAGE_TEXT.pieces;

  return (
    <RuleContextProvider value={ruleContext}>
      <div data-testid="extranet-page-pieces">
        <PageHeader
          title={text.title}
          subtitle={count === undefined ? undefined : PENDING_PIECES_TEXT.pageSubtitle(count)}
          backTo={extranetPath(tenantSlug)}
          breadcrumbs={[{ key: 'pieces', label: text.breadcrumb }]}
        />
        <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6 lg:px-8">
          <PendingPiecesListCard
            pieces={query.data ?? NO_PIECES}
            loading={query.isLoading}
            error={query.error?.message ?? null}
          />
        </div>
      </div>
    </RuleContextProvider>
  );
}
